import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../config/supabase";
import { User } from "@supabase/supabase-js";

interface GoalsManagementProps {
  user: User | null;
  darkMode: boolean;
}

interface Patient {
  user_id: string;
  name: string;
  surname: string;
  email: string;
  treatment_type?: string;
  current_daily_goal: number;
  current_weekly_goal: number;
  monday_goal_minutes?: number | null;
  tuesday_goal_minutes?: number | null;
  wednesday_goal_minutes?: number | null;
  thursday_goal_minutes?: number | null;
  friday_goal_minutes?: number | null;
  saturday_goal_minutes?: number | null;
  sunday_goal_minutes?: number | null;
}

interface ChildSummary {
  id: string;
  name: string;
  weekly_goal_minutes?: number;
  monday_goal_minutes?: number | null;
  tuesday_goal_minutes?: number | null;
  wednesday_goal_minutes?: number | null;
  thursday_goal_minutes?: number | null;
  friday_goal_minutes?: number | null;
  saturday_goal_minutes?: number | null;
  sunday_goal_minutes?: number | null;
}

interface GoalUpdateForm {
  monday_goal_minutes: number;
  tuesday_goal_minutes: number;
  wednesday_goal_minutes: number;
  thursday_goal_minutes: number;
  friday_goal_minutes: number;
  saturday_goal_minutes: number;
  sunday_goal_minutes: number;
}

const GoalsManagement: React.FC<GoalsManagementProps> = ({
  user,
  darkMode,
}) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [children, setChildren] = useState<ChildSummary[]>([]);
  const [selectedChild, setSelectedChild] = useState<ChildSummary | null>(null);
  const [goalForm, setGoalForm] = useState<GoalUpdateForm>({
    monday_goal_minutes: 240,
    tuesday_goal_minutes: 240,
    wednesday_goal_minutes: 240,
    thursday_goal_minutes: 240,
    friday_goal_minutes: 240,
    saturday_goal_minutes: 240,
    sunday_goal_minutes: 240,
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [status, setStatus] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    message: "",
  });
  const [sendingInvite, setSendingInvite] = useState(false);

  const fetchPatients = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      // First, get the patient IDs for this doctor
      const { data: accessData, error: accessError } = await supabase
        .from("doctor_user_access")
        .select("patient_id")
        .eq("doctor_id", user.id);

      if (accessError) throw accessError;

      if (!accessData || accessData.length === 0) {
        setPatients([]);
        return;
      }

      const patientIds = accessData.map((access) => access.patient_id);

      // Now fetch the user data for these patients (include treatment_type)
      const { data: patientsData, error } = await supabase
        .from("user_data")
        .select(
          `
          user_id,
          name,
          surname,
          email,
          treatment_type
        `,
        )
        .in("user_id", patientIds);

      if (error) throw error;

      // Separately fetch goals for these patients (including all daily goal columns)
      const { data: goalsData, error: goalsError } = await supabase
        .from("user_goals")
        .select(
          `
          user_id,
          weekly_goal_minutes,
          monday_goal_minutes,
          tuesday_goal_minutes,
          wednesday_goal_minutes,
          thursday_goal_minutes,
          friday_goal_minutes,
          saturday_goal_minutes,
          sunday_goal_minutes
        `,
        )
        .in("user_id", patientIds);

      if (goalsError) {
        console.warn("Error fetching goals:", goalsError);
        // Continue without goals data
      }

      // Create a goals lookup map
      const goalsMap = new Map();
      goalsData?.forEach((goal: any) => {
        goalsMap.set(goal.user_id, goal);
      });

      // Transform the data to match our Patient interface
      const transformedPatients =
        patientsData?.map((patient: any) => {
          const goals = goalsMap.get(patient.user_id);
          const mondayGoal = goals?.monday_goal_minutes;
          // Representative daily goal for list display:
          // - rest day (NULL) => 0 (we'll show "Rest day")
          // - otherwise use Monday goal if set, else fallback legacy column
          const representativeDailyGoal =
            mondayGoal === null
              ? 0
              : (mondayGoal ?? 240);
          return {
            user_id: patient.user_id,
            name: patient.name || "",
            surname: patient.surname || "",
            email: patient.email || "",
            treatment_type: patient.treatment_type || undefined,
            current_daily_goal: representativeDailyGoal,
            current_weekly_goal: goals?.weekly_goal_minutes || 1680,
            monday_goal_minutes: goals?.monday_goal_minutes ?? null,
            tuesday_goal_minutes: goals?.tuesday_goal_minutes ?? null,
            wednesday_goal_minutes: goals?.wednesday_goal_minutes ?? null,
            thursday_goal_minutes: goals?.thursday_goal_minutes ?? null,
            friday_goal_minutes: goals?.friday_goal_minutes ?? null,
            saturday_goal_minutes: goals?.saturday_goal_minutes ?? null,
            sunday_goal_minutes: goals?.sunday_goal_minutes ?? null,
          };
        }) || [];

      setPatients(transformedPatients);
    } catch (error) {
      console.error("Error fetching patients:", error);
      setStatus("Error loading patients");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchPatients();
    }
  }, [user, fetchPatients]);

  const sendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !inviteForm.email.trim()) return;

    try {
      setSendingInvite(true);
      setStatus("");

      const { data, error } = await supabase.rpc("send_doctor_invitation", {
        patient_email_param: inviteForm.email.trim(),
        message_param: inviteForm.message.trim() || null,
      });

      if (error) throw error;

      if (data.success) {
        setStatus("Invitation sent successfully!");
        setInviteForm({ email: "", message: "" });
        setShowInviteModal(false);
      } else {
        setStatus(`Error: ${data.error}`);
      }
    } catch (error: any) {
      console.error("Error sending invitation:", error);
      setStatus(`Error: ${error.message || "Failed to send invitation"}`);
    } finally {
      setSendingInvite(false);
    }
  };

  const revokeAccess = async (patientId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase.rpc("revoke_doctor_access", {
        p_doctor_id: user.id,
        p_patient_id: patientId,
      });

      if (error) throw error;

      setStatus("Patient access revoked successfully!");
      await fetchPatients(); // Refresh the patient list

      // Clear selected patient if it was the revoked one
      if (selectedPatient?.user_id === patientId) {
        setSelectedPatient(null);
      }
    } catch (error) {
      console.error("Error revoking access:", error);
      setStatus("Failed to revoke access. Please try again.");
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient);
    setChildren([]);
    setSelectedChild(null);
    const toFormValue = (v: number | null | undefined, fallback: number) =>
      v === null ? 0 : (v ?? fallback);
    setGoalForm({
      monday_goal_minutes: toFormValue(patient.monday_goal_minutes, 240),
      tuesday_goal_minutes: toFormValue(patient.tuesday_goal_minutes, 240),
      wednesday_goal_minutes: toFormValue(patient.wednesday_goal_minutes, 240),
      thursday_goal_minutes: toFormValue(patient.thursday_goal_minutes, 240),
      friday_goal_minutes: toFormValue(patient.friday_goal_minutes, 240),
      saturday_goal_minutes: toFormValue(patient.saturday_goal_minutes, 240),
      sunday_goal_minutes: toFormValue(patient.sunday_goal_minutes, 240),
    });
    setStatus("");
    if (patient.treatment_type === "child") {
      fetchChildrenForPatient(patient.user_id);
    }
  };

  // Fetch children and their goals for a child-treatment patient
  const fetchChildrenForPatient = useCallback(async (patientId: string) => {
    try {
      const { data: childrenData, error: childrenError } = await supabase
        .from("children")
        .select("id, name")
        .eq("user_id", patientId);

      if (childrenError) throw childrenError;

      const childIds = (childrenData || []).map((c: any) => c.id);

      if (childIds.length === 0) {
        setChildren([]);
        setSelectedChild(null);
        return;
      }

      const { data: goalsData, error: cgError } = await supabase
        .from("child_goals")
        .select(
          "child_id, weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes",
        )
        .in("child_id", childIds);

      if (cgError) {
        console.warn("Error fetching child goals:", cgError);
      }

      const goalsMap = new Map<string, any>();
      goalsData?.forEach((g: any) => goalsMap.set(g.child_id, g));

      const list: ChildSummary[] = (childrenData || []).map((c: any) => ({
        id: c.id,
        name: c.name || "Child",
        weekly_goal_minutes: goalsMap.get(c.id)?.weekly_goal_minutes,
        monday_goal_minutes: goalsMap.get(c.id)?.monday_goal_minutes ?? null,
        tuesday_goal_minutes: goalsMap.get(c.id)?.tuesday_goal_minutes ?? null,
        wednesday_goal_minutes:
          goalsMap.get(c.id)?.wednesday_goal_minutes ?? null,
        thursday_goal_minutes:
          goalsMap.get(c.id)?.thursday_goal_minutes ?? null,
        friday_goal_minutes: goalsMap.get(c.id)?.friday_goal_minutes ?? null,
        saturday_goal_minutes:
          goalsMap.get(c.id)?.saturday_goal_minutes ?? null,
        sunday_goal_minutes: goalsMap.get(c.id)?.sunday_goal_minutes ?? null,
      }));

      setChildren(list);
      setSelectedChild(list[0] || null);
      if (list[0]) {
        // null = rest day → 0 in form; undefined = not set → 240 fallback
        const toForm = (v: number | null | undefined) => v === null ? 0 : (v ?? 240);
        setGoalForm({
          monday_goal_minutes: toForm(list[0].monday_goal_minutes),
          tuesday_goal_minutes: toForm(list[0].tuesday_goal_minutes),
          wednesday_goal_minutes: toForm(list[0].wednesday_goal_minutes),
          thursday_goal_minutes: toForm(list[0].thursday_goal_minutes),
          friday_goal_minutes: toForm(list[0].friday_goal_minutes),
          saturday_goal_minutes: toForm(list[0].saturday_goal_minutes),
          sunday_goal_minutes: toForm(list[0].sunday_goal_minutes),
        });
      }
    } catch (err) {
      console.error("Error fetching children:", err);
      setChildren([]);
      setSelectedChild(null);
    }
  }, []);

  const handleGoalUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPatient || !user) return;

    try {
      setUpdating(true);
      setStatus("");

      const dayKeys = [
        "monday_goal_minutes",
        "tuesday_goal_minutes",
        "wednesday_goal_minutes",
        "thursday_goal_minutes",
        "friday_goal_minutes",
        "saturday_goal_minutes",
        "sunday_goal_minutes",
      ] as const;

      for (const k of dayKeys) {
        const v = goalForm[k];
        if (v === 0) continue; // rest day
        if (v < 30 || v > 480) {
          setStatus("Each day must be 30-480 minutes (or 0 for rest day).");
          setUpdating(false);
          return;
        }
      }

      const toNullable = (v: number) => (v === 0 ? null : v);
      if (selectedPatient.treatment_type === "child") {
        // Update child goals
        if (!selectedChild) {
          setStatus("Select a child to update goals.");
          setUpdating(false);
          return;
        }
        // Check if child goals exist first (match app logic)
        const { data: existing } = await supabase
          .from("child_goals")
          .select("child_id")
          .eq("child_id", selectedChild.id)
          .maybeSingle();

        let cgErr;
        if (existing) {
          // Update existing record (only day-specific columns)
          const { error } = await supabase
            .from("child_goals")
            .update({
              monday_goal_minutes: toNullable(goalForm.monday_goal_minutes),
              tuesday_goal_minutes: toNullable(goalForm.tuesday_goal_minutes),
              wednesday_goal_minutes: toNullable(
                goalForm.wednesday_goal_minutes,
              ),
              thursday_goal_minutes: toNullable(goalForm.thursday_goal_minutes),
              friday_goal_minutes: toNullable(goalForm.friday_goal_minutes),
              saturday_goal_minutes: toNullable(goalForm.saturday_goal_minutes),
              sunday_goal_minutes: toNullable(goalForm.sunday_goal_minutes),
              // weekly_goal_minutes is auto-calculated by DB trigger
              set_by_doctor_id: user.id,
            })
            .eq("child_id", selectedChild.id);
          cgErr = error;
        } else {
          // Insert new record (only day-specific columns)
          const { error } = await supabase.from("child_goals").insert({
            child_id: selectedChild.id,
            monday_goal_minutes: toNullable(goalForm.monday_goal_minutes),
            tuesday_goal_minutes: toNullable(goalForm.tuesday_goal_minutes),
            wednesday_goal_minutes: toNullable(goalForm.wednesday_goal_minutes),
            thursday_goal_minutes: toNullable(goalForm.thursday_goal_minutes),
            friday_goal_minutes: toNullable(goalForm.friday_goal_minutes),
            saturday_goal_minutes: toNullable(goalForm.saturday_goal_minutes),
            sunday_goal_minutes: toNullable(goalForm.sunday_goal_minutes),
            // weekly_goal_minutes is auto-calculated by DB trigger
            set_by_doctor_id: user.id,
          });
          cgErr = error;
        }
        if (cgErr) throw cgErr;

        // Snapshot child goals into history so past rest days are preserved
        await supabase.from("goal_history").upsert({
          user_id: user.id,
          child_id: selectedChild.id,
          effective_from: new Date().toISOString().split("T")[0],
          sunday_goal_minutes: toNullable(goalForm.sunday_goal_minutes),
          monday_goal_minutes: toNullable(goalForm.monday_goal_minutes),
          tuesday_goal_minutes: toNullable(goalForm.tuesday_goal_minutes),
          wednesday_goal_minutes: toNullable(goalForm.wednesday_goal_minutes),
          thursday_goal_minutes: toNullable(goalForm.thursday_goal_minutes),
          friday_goal_minutes: toNullable(goalForm.friday_goal_minutes),
          saturday_goal_minutes: toNullable(goalForm.saturday_goal_minutes),
        }, { onConflict: "user_id,child_id,effective_from" });

        setStatus("Child goals updated successfully!");

        // Refresh local child list goals
        setChildren(
          children.map((c) =>
            c.id === selectedChild.id
              ? {
                  ...c,
                  monday_goal_minutes: toNullable(goalForm.monday_goal_minutes),
                  tuesday_goal_minutes: toNullable(
                    goalForm.tuesday_goal_minutes,
                  ),
                  wednesday_goal_minutes: toNullable(
                    goalForm.wednesday_goal_minutes,
                  ),
                  thursday_goal_minutes: toNullable(
                    goalForm.thursday_goal_minutes,
                  ),
                  friday_goal_minutes: toNullable(goalForm.friday_goal_minutes),
                  saturday_goal_minutes: toNullable(
                    goalForm.saturday_goal_minutes,
                  ),
                  sunday_goal_minutes: toNullable(goalForm.sunday_goal_minutes),
                }
              : c,
          ),
        );
      } else {
        // Update or insert user goals for adult/self patients
        const { error } = await supabase.from("user_goals").upsert(
          {
            user_id: selectedPatient.user_id,
            monday_goal_minutes: toNullable(goalForm.monday_goal_minutes),
            tuesday_goal_minutes: toNullable(goalForm.tuesday_goal_minutes),
            wednesday_goal_minutes: toNullable(goalForm.wednesday_goal_minutes),
            thursday_goal_minutes: toNullable(goalForm.thursday_goal_minutes),
            friday_goal_minutes: toNullable(goalForm.friday_goal_minutes),
            saturday_goal_minutes: toNullable(goalForm.saturday_goal_minutes),
            sunday_goal_minutes: toNullable(goalForm.sunday_goal_minutes),
            set_by_doctor_id: user.id,
          },
          {
            onConflict: "user_id",
          },
        );

        if (error) throw error;

        // Snapshot user goals into history so past rest days are preserved
        await supabase.from("goal_history").upsert({
          user_id: selectedPatient.user_id,
          child_id: null,
          effective_from: new Date().toISOString().split("T")[0],
          sunday_goal_minutes: toNullable(goalForm.sunday_goal_minutes),
          monday_goal_minutes: toNullable(goalForm.monday_goal_minutes),
          tuesday_goal_minutes: toNullable(goalForm.tuesday_goal_minutes),
          wednesday_goal_minutes: toNullable(goalForm.wednesday_goal_minutes),
          thursday_goal_minutes: toNullable(goalForm.thursday_goal_minutes),
          friday_goal_minutes: toNullable(goalForm.friday_goal_minutes),
          saturday_goal_minutes: toNullable(goalForm.saturday_goal_minutes),
        }, { onConflict: "user_id,child_id,effective_from" });

        setStatus("Goals updated successfully!");

        // Update the patient in our local state
        setPatients(
          patients.map((patient) =>
            patient.user_id === selectedPatient.user_id
              ? {
                  ...patient,
                  // show a representative day (Monday) in the patient list
                  current_daily_goal: goalForm.monday_goal_minutes,
                  current_weekly_goal: patient.current_weekly_goal,
                  monday_goal_minutes: toNullable(goalForm.monday_goal_minutes),
                  tuesday_goal_minutes: toNullable(
                    goalForm.tuesday_goal_minutes,
                  ),
                  wednesday_goal_minutes: toNullable(
                    goalForm.wednesday_goal_minutes,
                  ),
                  thursday_goal_minutes: toNullable(
                    goalForm.thursday_goal_minutes,
                  ),
                  friday_goal_minutes: toNullable(goalForm.friday_goal_minutes),
                  saturday_goal_minutes: toNullable(
                    goalForm.saturday_goal_minutes,
                  ),
                  sunday_goal_minutes: toNullable(goalForm.sunday_goal_minutes),
                }
              : patient,
          ),
        );

        // Update selected patient
        setSelectedPatient({
          ...selectedPatient,
          current_daily_goal: goalForm.monday_goal_minutes,
          current_weekly_goal: selectedPatient.current_weekly_goal,
          monday_goal_minutes: toNullable(goalForm.monday_goal_minutes),
          tuesday_goal_minutes: toNullable(goalForm.tuesday_goal_minutes),
          wednesday_goal_minutes: toNullable(goalForm.wednesday_goal_minutes),
          thursday_goal_minutes: toNullable(goalForm.thursday_goal_minutes),
          friday_goal_minutes: toNullable(goalForm.friday_goal_minutes),
          saturday_goal_minutes: toNullable(goalForm.saturday_goal_minutes),
          sunday_goal_minutes: toNullable(goalForm.sunday_goal_minutes),
        });
      }
    } catch (error) {
      console.error("Error updating goals:", error);
      setStatus("Failed to update goals. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round((minutes % 60) * 10) / 10; // Round to 1 decimal
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading patients...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2
          className={`text-3xl font-bold mb-2 ${
            darkMode ? "text-white" : "text-brand-dark-blue"
          }`}
        >
          Patient Goals Management
        </h2>
        <p
          className={`text-lg ${darkMode ? "text-gray-300" : "text-gray-600"}`}
        >
          Set and manage therapy goals for your patients
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Patient List */}
        <div
          className={`rounded-lg p-6 ${
            darkMode ? "bg-gray-800" : "bg-white"
          } shadow-lg`}
        >
          <div className="flex justify-between items-center mb-4">
            <h3
              className={`text-xl font-semibold ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Your Patients ({patients.length})
            </h3>
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-3 py-1 bg-brand-cyan text-white rounded-md hover:bg-brand-dark-blue transition-colors text-sm"
            >
              Invite Patient
            </button>
          </div>

          {patients.length === 0 ? (
            <div
              className={`text-center py-8 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <p>No patients assigned to you yet.</p>
              <p className="text-sm mt-2">
                Click "Add Patient" to assign patients to your care.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {patients.map((patient) => (
                <div
                  key={patient.user_id}
                  onClick={() => handlePatientSelect(patient)}
                  className={`p-4 rounded-lg cursor-pointer transition-all ${
                    selectedPatient?.user_id === patient.user_id
                      ? darkMode
                        ? "bg-blue-900 border-blue-400"
                        : "bg-blue-50 border-blue-300"
                      : darkMode
                        ? "bg-gray-700 hover:bg-gray-600 border-gray-600"
                        : "bg-gray-50 hover:bg-gray-100 border-gray-200"
                  } border-2`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4
                        className={`font-semibold ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {patient.name && patient.surname
                          ? `${patient.name} ${patient.surname}`
                          : "Unnamed Patient"}
                      </h4>
                      <p
                        className={`text-sm ${
                          darkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {patient.email}
                      </p>
                    </div>
                    <div className="text-right">
                      <div
                        className={`text-xs ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Current Goals
                      </div>
                      <div
                        className={`text-sm font-medium ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Daily:{" "}
                        {patient.current_daily_goal === 0
                          ? "Rest day"
                          : formatTime(patient.current_daily_goal)}
                      </div>
                      <div
                        className={`text-sm font-medium mb-2 ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Weekly: {formatTime(patient.current_weekly_goal)}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent patient selection
                          if (
                            window.confirm(
                              "Are you sure you want to remove access to this patient?",
                            )
                          ) {
                            revokeAccess(patient.user_id);
                          }
                        }}
                        className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Goal Setting Form */}
        <div
          className={`rounded-lg p-6 ${
            darkMode ? "bg-gray-800" : "bg-white"
          } shadow-lg`}
        >
          <h3
            className={`text-xl font-semibold mb-4 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Set Goals
          </h3>

          {selectedPatient ? (
            <div>
              {selectedPatient.treatment_type === "child" && (
                <div className="mb-6">
                  <div
                    className={`${
                      darkMode ? "text-white" : "text-gray-900"
                    } mb-2`}
                  >
                    Select Child
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={selectedChild?.id || ""}
                      onChange={(e) => {
                        const child =
                          children.find((c) => c.id === e.target.value) || null;
                        setSelectedChild(child);
                        if (child) {
                          // null = rest day → 0 in form; undefined = not set → 240 fallback
                          const toForm = (v: number | null | undefined) => v === null ? 0 : (v ?? 240);
                          setGoalForm({
                            monday_goal_minutes: toForm(child.monday_goal_minutes),
                            tuesday_goal_minutes: toForm(child.tuesday_goal_minutes),
                            wednesday_goal_minutes: toForm(child.wednesday_goal_minutes),
                            thursday_goal_minutes: toForm(child.thursday_goal_minutes),
                            friday_goal_minutes: toForm(child.friday_goal_minutes),
                            saturday_goal_minutes: toForm(child.saturday_goal_minutes),
                            sunday_goal_minutes: toForm(child.sunday_goal_minutes),
                          });
                        }
                      }}
                      className={`px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                        darkMode
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "border-gray-300"
                      }`}
                    >
                      <option value="">Select a child...</option>
                      {children.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() =>
                        fetchChildrenForPatient(selectedPatient.user_id)
                      }
                      className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                        darkMode
                          ? "bg-gray-600 text-white hover:bg-gray-500"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      Load Children
                    </button>
                  </div>
                </div>
              )}
              <div
                className={`mb-6 p-4 rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                <h4
                  className={`font-semibold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Selected Patient:
                </h4>
                <p className={darkMode ? "text-gray-300" : "text-gray-700"}>
                  {selectedPatient.name && selectedPatient.surname
                    ? `${selectedPatient.name} ${selectedPatient.surname}`
                    : "Unnamed Patient"}
                </p>
                <p
                  className={`text-sm ${
                    darkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  {selectedPatient.email}
                </p>
                {selectedPatient.treatment_type === "child" && (
                  <div
                    className={`mt-2 text-xs ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    This is a child-treatment account. Update child goals below.
                  </div>
                )}
              </div>

              <form onSubmit={handleGoalUpdate} className="space-y-4">
                <div
                  className={`text-sm ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Set each day’s goal. Enter <b>0</b> for rest day (saved as{" "}
                  <code>NULL</code>). Weekly goal is auto-calculated.
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(
                    [
                      ["Sunday", "sunday_goal_minutes"],
                      ["Monday", "monday_goal_minutes"],
                      ["Tuesday", "tuesday_goal_minutes"],
                      ["Wednesday", "wednesday_goal_minutes"],
                      ["Thursday", "thursday_goal_minutes"],
                      ["Friday", "friday_goal_minutes"],
                      ["Saturday", "saturday_goal_minutes"],
                    ] as const
                  ).map(([label, key]) => {
                    const value = goalForm[key];
                    const isRestDay = value === 0;

                    return (
                      <div key={key}>
                        <label
                          className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <span>{label}</span>
                          {isRestDay && (
                            <span
                              className="text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-0.5 rounded"
                              title="Rest Day"
                            >
                              🛌 Rest
                            </span>
                          )}
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="8"
                            value={Math.floor(value / 60)}
                            onChange={(e) => {
                              let hours = parseInt(e.target.value, 10) || 0;
                              if (hours > 8) hours = 8;
                              if (hours < 0) hours = 0;
                              const currentMinutes = value % 60;
                              let total = hours * 60 + currentMinutes;
                              if (total > 480) total = 480;
                              setGoalForm({
                                ...goalForm,
                                [key]: total,
                              });
                            }}
                            className={`w-20 px-3 py-3 border rounded-lg text-center focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                              isRestDay
                                ? darkMode
                                  ? "bg-green-900/20 border-green-600 text-green-200"
                                  : "bg-green-50 border-green-300 text-green-800"
                                : darkMode
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "border-gray-300"
                            }`}
                            placeholder="0"
                          />
                          <span className={`text-lg font-bold ${darkMode ? "text-gray-300" : "text-gray-600"}`}>:</span>
                          <input
                            type="number"
                            min="0"
                            max="59"
                            value={value % 60}
                            onChange={(e) => {
                              let mins = parseInt(e.target.value, 10) || 0;
                              if (mins > 59) mins = 59;
                              if (mins < 0) mins = 0;
                              const currentHours = Math.floor(value / 60);
                              let total = currentHours * 60 + mins;
                              if (total > 480) total = 480;
                              setGoalForm({
                                ...goalForm,
                                [key]: total,
                              });
                            }}
                            className={`w-20 px-3 py-3 border rounded-lg text-center focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                              isRestDay
                                ? darkMode
                                  ? "bg-green-900/20 border-green-600 text-green-200"
                                  : "bg-green-50 border-green-300 text-green-800"
                                : darkMode
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "border-gray-300"
                            }`}
                            placeholder="00"
                          />
                          <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>h : min</span>
                        </div>
                        {isRestDay && (
                          <p
                            className={`text-xs mt-1 ${
                              darkMode ? "text-green-300" : "text-green-600"
                            }`}
                          >
                            Rest day - no goal
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() =>
                      setGoalForm({
                        monday_goal_minutes: 240,
                        tuesday_goal_minutes: 240,
                        wednesday_goal_minutes: 240,
                        thursday_goal_minutes: 240,
                        friday_goal_minutes: 240,
                        saturday_goal_minutes: 240,
                        sunday_goal_minutes: 240,
                      })
                    }
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      darkMode
                        ? "bg-gray-600 text-white hover:bg-gray-500"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    Default (4h/28h)
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setGoalForm({
                        monday_goal_minutes: 180,
                        tuesday_goal_minutes: 180,
                        wednesday_goal_minutes: 180,
                        thursday_goal_minutes: 180,
                        friday_goal_minutes: 180,
                        saturday_goal_minutes: 180,
                        sunday_goal_minutes: 180,
                      })
                    }
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      darkMode
                        ? "bg-gray-600 text-white hover:bg-gray-500"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    Light (3h/21h)
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setGoalForm({
                        monday_goal_minutes: 300,
                        tuesday_goal_minutes: 300,
                        wednesday_goal_minutes: 300,
                        thursday_goal_minutes: 300,
                        friday_goal_minutes: 300,
                        saturday_goal_minutes: 300,
                        sunday_goal_minutes: 300,
                      })
                    }
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      darkMode
                        ? "bg-gray-600 text-white hover:bg-gray-500"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    Intensive (5h/35h)
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={
                    updating ||
                    (selectedPatient?.treatment_type === "child" &&
                      !selectedChild)
                  }
                  className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform-gpu hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updating
                    ? "Updating Goals..."
                    : selectedPatient?.treatment_type === "child"
                      ? "Update Child Goals"
                      : "Update Goals"}
                </button>

                {status && (
                  <div
                    className={`p-3 rounded-lg text-sm font-medium ${
                      status.includes("success")
                        ? darkMode
                          ? "bg-green-900 text-green-300 border border-green-700"
                          : "bg-green-50 text-green-800 border border-green-200"
                        : darkMode
                          ? "bg-red-900 text-red-300 border border-red-700"
                          : "bg-red-50 text-red-800 border border-red-200"
                    }`}
                  >
                    {status}
                  </div>
                )}
              </form>
            </div>
          ) : (
            <div
              className={`text-center py-8 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <p>Select a patient from the list to set their goals.</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      {patients.length > 0 && (
        <div
          className={`rounded-lg p-6 ${
            darkMode ? "bg-gray-800" : "bg-white"
          } shadow-lg`}
        >
          <h3
            className={`text-xl font-semibold mb-4 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Quick Statistics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              className={`p-4 rounded-lg ${
                darkMode ? "bg-gray-700" : "bg-gray-50"
              }`}
            >
              <div
                className={`text-2xl font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {patients.length}
              </div>
              <div
                className={`text-sm ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Total Patients
              </div>
            </div>
            <div
              className={`p-4 rounded-lg ${
                darkMode ? "bg-gray-700" : "bg-gray-50"
              }`}
            >
              <div
                className={`text-2xl font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {formatTime(
                  Math.round(
                    patients.reduce((sum, p) => sum + p.current_daily_goal, 0) /
                      patients.length,
                  ),
                )}
              </div>
              <div
                className={`text-sm ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Avg Daily Goal
              </div>
            </div>
            <div
              className={`p-4 rounded-lg ${
                darkMode ? "bg-gray-700" : "bg-gray-50"
              }`}
            >
              <div
                className={`text-2xl font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {formatTime(
                  Math.round(
                    patients.reduce(
                      (sum, p) => sum + p.current_weekly_goal,
                      0,
                    ) / patients.length,
                  ),
                )}
              </div>
              <div
                className={`text-sm ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Avg Weekly Goal
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invitation Modal */}
      {showInviteModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowInviteModal(false)}
        >
          <div
            className={`p-6 rounded-lg max-w-md w-full mx-4 ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3
                className={`text-xl font-semibold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Invite Patient
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className={`text-2xl ${
                  darkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                ×
              </button>
            </div>

            <form onSubmit={sendInvitation} className="space-y-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Patient Email *
                </label>
                <input
                  type="email"
                  maxLength={254}
                  value={inviteForm.email}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, email: e.target.value })
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "border-gray-300"
                  }`}
                  placeholder="patient@example.com"
                  required
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Personal Message (Optional)
                </label>
                <textarea
                  maxLength={1000}
                  value={inviteForm.message}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, message: e.target.value })
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "border-gray-300"
                  }`}
                  placeholder="Hi! I'd like to help with your therapy..."
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    darkMode
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingInvite || !inviteForm.email.trim()}
                  className="px-4 py-2 bg-brand-cyan text-white rounded-lg hover:bg-brand-dark-blue transition-colors disabled:opacity-50"
                >
                  {sendingInvite ? "Sending..." : "Send Invitation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoalsManagement;
